import { getResponseError, MPOST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';
import type { APP_NAME } from 'types/misc.ts';
import { Button } from 'ui/component/Button.tsx';
import { Input } from 'ui/control/Input.tsx';

type FormValues = {
  email: string;
};

type ResetPasswordRequestValues = FormValues & { app: APP_NAME };

export const PasswordRequest: React.FC = () => {
  const { t } = useTranslation();
  const [isRequestSent, setIsRequestSent] = React.useState(false);

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: { email: '' },
  });

  const { handleSubmit, formState } = formMethods;

  const { trigger, isMutating, error } = useSWRMutation<FormValues, any, string, ResetPasswordRequestValues>('/password/request', MPOST);

  const onSubmit = async (values: FormValues) => {
    await trigger({ ...values, app: 'admin' });
    setIsRequestSent(true);
  };

  return (
    <main className="auth-gradient-background fixed top-0 h-screen w-screen overflow-auto pt-16">
      <FormProvider {...formMethods}>
        <form className="paper m-auto w-[480px] border" autoCorrect={'off'} autoComplete={'off'} onSubmit={handleSubmit(onSubmit)} noValidate={true}>
          <div className="flex flex-col items-center gap-4 p-8">
            <div className="mb-6 flex w-full items-center justify-center">
              <img src={'/atlas-path-logo.svg'} width={380} alt="Atlas Path Logo" />
            </div>
            <h1 className="text-center font-bold text-2xl">{t('auth.recoverPassword.title')}</h1>
            <p className="body1 mb-4 inline text-center">{isRequestSent ? t('auth.recoverPassword.emailSent') : t('auth.recoverPassword.enterEmail')}</p>
            <div className="flex w-full flex-col items-center gap-4">
              {!isRequestSent && (
                <>
                  <Input
                    className="w-full"
                    name={'email'}
                    label={t('auth.recoverPassword.emailLabel')}
                    autoFocus={true}
                    type={'email'}
                    registerOptions={{
                      validate: { required: (value) => (value ? undefined : t('auth.recoverPassword.emailRequired')) },
                    }}
                  />
                  {error && (
                    <section className="mt-4 grid grid-flow-col justify-center text-center text-red-700">{getResponseError(error, t('auth.recoverPassword.requestError'))}</section>
                  )}
                  <Button className="blue mt-4 gap-1" type="submit" disabled={!formState.isValid} loading={isMutating}>
                    {t('actions.submit')}
                  </Button>
                </>
              )}
              {isRequestSent && (
                <Link to="/auth" className="contents">
                  <Button className="blue mt-4 gap-1">{t('actions.backToLogin')}</Button>
                </Link>
              )}
            </div>
            {!isRequestSent && (
              <Link to="/auth" className="contents">
                <Button className="text-blue">{t('actions.backToLogin')}</Button>
              </Link>
            )}
          </div>
        </form>
      </FormProvider>
    </main>
  );
};
